/**
 * User-specific dropdown button with avatar icon.
 * Used for user-related actions like profile, settings, logout, etc.
 * Features click-outside detection for auto-closing.
 */

import React, { useState, useRef, useEffect, ReactNode } from 'react'
import { CircleButton } from '../ui_reusable/styles/Button.styles' // Updated import
import { dropdownContainer, dropdownMenu } from './styles/DropdownButton.styles'
import AvatarIconStyle, {
  AvatarIcon,
} from '../ui_reusable/styles/AvatarIcon.styles'
import { useAuth } from '../../context/AuthContext'

// Replace Button definition with CircleButton
const Button = CircleButton

interface DropdownButtonUserProps {
  children?: ReactNode
}

const DropdownButtonUser: React.FC<DropdownButtonUserProps> = ({
  children,
}) => {
  // Track dropdown open/close state
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()

  // Click outside handler to auto-close dropdown
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div css={dropdownContainer} ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        {user?.avatar ? (
          <AvatarIcon src={user?.avatar} alt={user.name} />
        ) : (
          <div css={AvatarIconStyle} />
        )}
      </Button>
      <div
        css={dropdownMenu}
        style={{ width: '16rem' }}
        className={isOpen ? 'open' : ''}
      >
        {React.Children.map(children, (child: any) =>
          child && typeof child.type !== 'string'
            ? React.cloneElement(child, { setIsOpen: setIsOpen || (() => {}) })
            : child,
        )}
      </div>
    </div>
  )
}

export default DropdownButtonUser
